import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../common';
import {
  buildReceiveSupplyOrderPayload,
  formatSupplyOrderQuantity,
  getSupplyOrderApiErrorMessage,
} from '../utils/supplyOrdersUtils';

const ReceiveSupplyOrderDialog = ({
  open,
  onClose,
  order,
  onReceive,
  isProcessing = false,
}) => {
  const [formState, setFormState] = useState({
    received_at: '',
    supplier_document_type: '',
    supplier_document_number: '',
    carrier_name: '',
    notes: '',
  });
  const [itemQuantities, setItemQuantities] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  const receivableItems = useMemo(
    () =>
      (order?.items || []).filter(
        (item) =>
          Number(item.pending_quantity) > 0 &&
          !['cancelled', 'closed_with_difference'].includes(item.status)
      ),
    [order?.items]
  );

  useEffect(() => {
    if (open) {
      const today = new Date().toISOString().split('T')[0];
      setFormState({
        received_at: today,
        supplier_document_type: '',
        supplier_document_number: '',
        carrier_name: '',
        notes: '',
      });
      const initialQuantities = {};
      receivableItems.forEach((item) => {
        initialQuantities[item.uuid] = '';
      });
      setItemQuantities(initialQuantities);
      setErrorMessage('');
    }
  }, [open, order?.uuid, receivableItems]);

  const handleQuantityChange = (itemUuid, value) => {
    setItemQuantities((prev) => ({
      ...prev,
      [itemUuid]: value,
    }));
    if (errorMessage) setErrorMessage('');
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleConfirm = async () => {
    const itemsWithQuantities = receivableItems.map((item) => ({
      ...item,
      receiveQuantity: itemQuantities[item.uuid],
    }));

    for (const item of itemsWithQuantities) {
      const quantity = Number(item.receiveQuantity);
      if (!quantity) continue;
      const pending = Number(item.pending_quantity) || 0;
      if (quantity > pending) {
        setErrorMessage(
          `La cantidad a recibir no puede superar la pendiente (${formatSupplyOrderQuantity(pending)}) para ${item.article_name_snapshot}.`
        );
        return;
      }
    }

    const payload = buildReceiveSupplyOrderPayload(formState, itemsWithQuantities);
    if (!payload) {
      setErrorMessage('Ingresá al menos una cantidad a recibir mayor a cero.');
      return;
    }

    try {
      setErrorMessage('');
      await onReceive(payload);
      onClose();
    } catch (error) {
      setErrorMessage(getSupplyOrderApiErrorMessage(error, 'No se pudo registrar la recepción'));
    }
  };

  if (!open) return null;

  return (
    <DialogContainer open={open} onClose={onClose} size="large" minHeight="60vh">
      <DialogTitle>Registrar recepción — {order?.order_code || 'Orden'}</DialogTitle>
      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {order?.source_type === 'warehouse' && (
            <Alert severity="warning">
              Esta orden descuenta stock del depósito origen. Si no hay stock suficiente, la
              operación será rechazada por el backend.
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <CustomTextField
              label="Fecha de recepción"
              name="received_at"
              type="date"
              value={formState.received_at}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <CustomTextField
              label="Tipo de documento"
              name="supplier_document_type"
              value={formState.supplier_document_type}
              onChange={handleInputChange}
              fullWidth
            />
            <CustomTextField
              label="Número de documento"
              name="supplier_document_number"
              value={formState.supplier_document_number}
              onChange={handleInputChange}
              fullWidth
            />
            <CustomTextField
              label="Transportista"
              name="carrier_name"
              value={formState.carrier_name}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>

          <CustomTextField
            label="Notas"
            name="notes"
            value={formState.notes}
            onChange={handleInputChange}
            multiline
            minRows={2}
            fullWidth
          />

          <Typography variant="subtitle1" fontWeight={700}>
            Cantidades a recibir
          </Typography>

          <GeneralTableBox>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <HeadTableCell>Artículo</HeadTableCell>
                  <HeadTableCell>Solicitada</HeadTableCell>
                  <HeadTableCell>Recibida</HeadTableCell>
                  <HeadTableCell>Pendiente</HeadTableCell>
                  <HeadTableCell>Recibir ahora</HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receivableItems.length === 0 ? (
                  <TableRow>
                    <RowTableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                      No hay artículos pendientes de recepción
                    </RowTableCell>
                  </TableRow>
                ) : (
                  receivableItems.map((item) => (
                    <TableRow key={item.uuid}>
                      <RowTableCell>
                        {item.article_code_snapshot} — {item.article_name_snapshot}
                      </RowTableCell>
                      <RowTableCell>
                        {formatSupplyOrderQuantity(item.requested_quantity)}
                      </RowTableCell>
                      <RowTableCell>
                        {formatSupplyOrderQuantity(item.received_quantity)}
                      </RowTableCell>
                      <RowTableCell>
                        {formatSupplyOrderQuantity(item.pending_quantity)}
                      </RowTableCell>
                      <RowTableCell>
                        <CustomTextField
                          type="number"
                          value={itemQuantities[item.uuid] ?? ''}
                          onChange={(event) =>
                            handleQuantityChange(item.uuid, event.target.value)
                          }
                          inputProps={{
                            min: 0,
                            max: item.pending_quantity,
                            step: '0.001',
                          }}
                          size="small"
                          sx={{ maxWidth: 120 }}
                        />
                      </RowTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        </Box>
      </DialogBody>
      <DialogActions
        handleClose={onClose}
        handleSave={handleConfirm}
        saveLabel="Confirmar recepción"
        saveButtonProps={{ isLoading: isProcessing }}
      />
    </DialogContainer>
  );
};

export default ReceiveSupplyOrderDialog;
